// src/utils/authors.js
// Shared helpers for formatting author lists consistently across emails,
// invitations, and manuscript displays.

/**
 * Extract a display name from a single author entry.
 * Handles plain strings and author objects with various name fields.
 * @param {string|Object} author
 * @returns {string}
 */
export function formatAuthorName(author) {
  if (author == null) return '';
  if (typeof author === 'string') return author.trim();

  if (typeof author === 'object') {
    const fullName =
      author.name ||
      author.authors_fullname ||
      author.fullname ||
      author.fullName ||
      author.author_name ||
      [author.prefix, author.firstname || author.firstName, author.lastname || author.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
    return fullName || '';
  }

  return String(author).trim();
}

/**
 * Format an authors list into a single comma-separated string of names.
 * Accepts an array of strings, an array of objects, a single string, or a
 * JSON string that parses into any of those.
 * @param {string|Array|Object} authors
 * @param {string} [separator=', ']
 * @returns {string}
 */
export function formatAuthors(authors, separator = ', ') {
  if (!authors) return '';

  let list = authors;

  if (typeof list === 'string') {
    const trimmed = list.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        list = JSON.parse(trimmed);
      } catch (e) {
        // Not valid JSON — treat as a single name or a plain delimited string.
        return trimmed;
      }
    } else {
      return trimmed;
    }
  }

  if (!Array.isArray(list)) {
    // A single author object.
    return formatAuthorName(list);
  }

  return list
    .map(formatAuthorName)
    .filter(Boolean)
    .join(separator);
}
